import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Row, Col, Spin, Result, Button } from "antd";
import { useHistory } from "react-router-dom";
import FloatingFormActions from "../../components/forms/FloatingFormActions";
import { InputItem } from "../../components/forms/FormItems";
import FormActions from "../../components/forms/FormActions";
import AutoCompleteItem from "../../components/forms/AutoCompleteItem";
import { SettingsDataSelect } from "../../components/forms/AutoCompleteFormItem";
import PhoneNumberItem from "../../components/forms/PhoneNumberItem";
import Centered from "../../components/layouts/Centered";
import Surface from "../../components/layouts/Surface";
import ContentHolder from "../../components/layouts/ContentHolder";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import FormHeader from "../../components/forms/FormHeader";
import Spacer from "../../components/layouts/Spacer";
import ResponsiveItemsWrapper from "../../components/layouts/ResponsiveItemsWrapper";
import { getChangedItems } from "../../components/util/helpers";
import { useParams } from "react-router-dom";
import {
  loadRecord,
  deleteRecord,
  commitChanges,
  reset,
  setChangedFormItems,
} from "./branchDetailsSlice";
import { fetchUserProfile } from "../user/UserSlice";
import { FormattedMessage } from "react-intl";
import AnimationWrapper from "../../components/animations/AnimationWrapper";
import { isAllowedFeature } from "../organization/organizationSlice";
var flatten = require("flat");

const BranchDetails = (props) => {
  const dispatch = useDispatch();
  const { mode } = props;
  const [form] = Form.useForm();
  const activeBranch = useSelector((state) => state.branchDetails.activeBranch);
  const changedItems = useSelector(
    (state) => state.branchDetails.changedItems.changed
  );
  const fetchLoading = useSelector(
    (state) => state.pendingActions["branchDetails/loadBranch"]
  );
  const saveLoading = useSelector(
    (state) => state.pendingActions["branchDetails/update"]
  );
  const deleteLoading = useSelector(
    (state) => state.pendingActions["branchDetails/delete"]
  );
  let history = useHistory();
  const intl = useIntl();
  let { id } = useParams();
  useEffect(() => {
    if (mode === "edit") {
      dispatch(loadRecord(id));
    }
    return function cleanup() {
      dispatch(reset());
      dispatch(fetchUserProfile());
    };
  }, [dispatch, id, mode]);

  useEffect(() => {
    if (!fetchLoading && (activeBranch.id !== undefined || mode === "new")) {
      const flat = flatten(activeBranch, { safe: true });
      form.setFieldsValue(flat);
    }
  }, [activeBranch, form, history, mode, fetchLoading]);

  const onConfirm = async () => {
    const action = await dispatch(deleteRecord(activeBranch.id));
    if (!action.error) {
      history.goBack();
    }
  };

  const onDecline = () => {
    history.goBack();
  };

  const onValuesChange = (changedValues, allValues) => {
    const arr = getChangedItems(
      changedValues,
      allValues,
      changedItems,
      activeBranch
    );
    dispatch(setChangedFormItems({ changed: arr, values: allValues }));
  };

  const onFinish = async () => {
    const action = await dispatch(commitChanges());
    if (!action.error) {
      dispatch(fetchUserProfile());
      dispatch(loadRecord(activeBranch.id));
    }
  };

  if (fetchLoading) {
    return (
      <Centered>
        <Spin size="large" />
      </Centered>
    );
  }

  // If data is loaded and activeBranch is available or mode is new
  if (activeBranch?.id !== undefined || mode === "new") {
    return (
      <AnimationWrapper duration={0.6} overflow={true}>
        <Form
          layout="vertical"
          onValuesChange={onValuesChange}
          onFinish={onFinish}
          form={form}
        >
          <Surface>
            <FormHeader
              title={<FormattedMessage id="app.forms.branchDetails" />}
            />
            <Row>
              <Col xs={24} sm={24} md={24} lg={21} xl={15} xxl={10}>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem name="name" required={true} category="branch" />
                  </div>

                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <PhoneNumberItem name="phone_number" category="branch" />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <PhoneNumberItem name="mobile_number" category="branch" />
                  </div>

                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <PhoneNumberItem name="fax_number" category="branch" />
                  </div>
                </ResponsiveItemsWrapper>

                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem name="email" email={true} category="branch" />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}></div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem
                      required={true}
                      name="salary_id"
                      category="branch"
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem
                      number={true}
                      required={true}
                      name="btl_accounting_number"
                      category="branch"
                      placeholder={intl.formatMessage({
                        id: "app.forms.branch.btl_number_placeholder",
                      })}
                    />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem
                      number={true}
                      required={isAllowedFeature("accounting_tag")}
                      name="accounting_number_tag"
                      category="branch"
                      placeholder={intl.formatMessage({
                        id: "app.forms.branch.accounting_number_tag",
                      })}
                      oneToNinetyNineNumbers={true}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem name="credit_account_number" category="branch" />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <SettingsDataSelect
                      name="service_areas"
                      category="branch"
                      settingsdataName={"system_tables/service_areas"}
                      mode="multiple"
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <SettingsDataSelect
                      name="group_id"
                      category="branch"
                      settingsdataName={"system_tables/branch_groups"}
                      required={process.env.REACT_APP_CONFIG === "matav"}
                    />
                  </div>
                </ResponsiveItemsWrapper>

                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <SettingsDataSelect
                      name="subprojects"
                      category="branch"
                      settingsdataName={"system_tables/subprojects"}
                      mode="multiple"
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="ok2go_revaha_code"
                      category="branch"
                      number={true}
                    />
                  </div>
                </ResponsiveItemsWrapper>

                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="ok2go_code"
                      category="branch"
                      number={true}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem name="ok2go_password" category="branch" />
                  </div>
                </ResponsiveItemsWrapper>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="supporting_community_accounting_number"
                      category="branch"
                      number={true}
                    />
                  </div>
                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="day_center_accounting_number"
                      category="branch"
                      number={true}
                    />
                  </div>
                </ResponsiveItemsWrapper>
              </Col>
            </Row>
          </Surface>

          <Surface>
            <FormHeader
              title={<FormattedMessage id="app.forms.branchAddress" />}
            />
            <Row>
              <Col xs={24} sm={24} md={24} lg={21} xl={15} xxl={10}>
                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <AutoCompleteItem
                      name="address.city"
                      category="branch"
                      city={true}
                    />
                  </div>

                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem
                      name="address.postal_code"
                      postalCode={true}
                      category="branch"
                    />
                  </div>
                </ResponsiveItemsWrapper>

                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem name="address.street" category="branch" />
                  </div>

                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}>
                    <InputItem name="address.house_number" category="branch" />
                  </div>
                </ResponsiveItemsWrapper>

                <ResponsiveItemsWrapper>
                  <div style={{ flex: 1 }}>
                    <InputItem name="mailbox_number" category="branch" />
                  </div>

                  <Spacer horizontal={true} />
                  <div style={{ flex: 1 }}></div>
                </ResponsiveItemsWrapper>
              </Col>
            </Row>
          </Surface>
          <FloatingFormActions
            resource={"branches"}
            action={"editor"}
            onCancel={onDecline}
            mode={"edit"}
            onDecline={onDecline}
            hideDelete={true}
            hideDecline={true}
            saveLoading={saveLoading}
            saveText={intl.formatMessage({
              id: "app.actions.saveChanges",
            })}
            show={changedItems.length > 0}
          />
          <FormActions
            resource={"branches"}
            action={"editor"}
            category={"branch"}
            mode={mode}
            record={activeBranch}
            hideDecline={true}
            hidePrimary={true}
            onConfirm={onConfirm}
            saveLoading={saveLoading}
            deleteLoading={deleteLoading}
          />
        </Form>
      </AnimationWrapper>
    );
  }

  // If data is loaded and no active branch found
  if (activeBranch.rejected)
    return (
      <ContentHolder>
        <Result
          status="warning"
          title={intl.formatMessage({
            id: "app.errors.failFetchBranch",
          })}
          extra={
            <Button
              type="primary"
              key="branches"
              onClick={() => {
                history.goBack();
              }}
            >
              {intl.formatMessage({
                id: "app.actions.goToBranches",
              })}
            </Button>
          }
        />
      </ContentHolder>
    );
};

BranchDetails.propTypes = {
  mode: PropTypes.string,
};

export default BranchDetails;
